import { CarIcon, CheckIcon, HomeIcon, InfoIcon, Share2Icon, SquarePenIcon, UserCogIcon } from 'lucide-react'

import { JourneyStage, JourneyView, useTarget } from '../../journey'

import {
  CONFIRM_MESSAGE,
  HIGHLIGHT_OPTIONS,
  isRowHidden,
  PERMISSIONS,
  PLATFORM_BADGE,
  PLATFORM_OPTIONS,
  ROLE_HINT,
  STAR_BADGE,
  TEAM,
  toneClass,
  VEHICLES,
} from './content'

import type { ReactNode } from 'react'
import type { ChileautosHighlightsState } from './journey'

interface ViewProps {
  state: ChileautosHighlightsState
}

export const Simulator = ({ state }: ViewProps) => (
  <JourneyStage
    state={state}
    user={state.seller ? { name: 'Camilo', role: 'Vendedor' } : { name: 'Marcelo', role: 'Administrador' }}
    nav={[
      { label: 'Inicio', icon: HomeIcon, active: state.navActive === 'inicio' },
      { label: 'Vehículos', icon: CarIcon },
      {
        label: 'Publicaciones',
        icon: Share2Icon,
        target: 'navPublicaciones',
        active: state.navActive === 'publicaciones',
      },
      { label: 'Usuarios', icon: UserCogIcon, target: 'navUsuarios', active: state.navActive === 'usuarios' },
    ]}
  >
    <HomeView />
    <LoadingView />
    <UsersView />
    <PermissionsView state={state} />
    <PublishView state={state} />

    <ConfirmDialog open={state.confirmOpen} />

    <div
      className="absolute right-1.5 top-1.5 z-40 rounded bg-[#1c1917] px-1.5 py-1 font-mono text-[7px] uppercase tracking-wider text-white transition-all duration-300"
      style={{ opacity: state.relogged ? 1 : 0, transform: `translateY(${state.relogged ? 0 : -4}px)` }}
    >
      Volvió a iniciar sesión
    </div>

    <div
      className="absolute bottom-2 right-2 z-50 flex items-center gap-1 rounded bg-[#1c1917] px-2 py-1.5 text-[7px] text-white transition-all duration-300"
      style={{ opacity: state.toast ? 1 : 0, transform: `translateY(${state.toast ? '0' : '0.75rem'})` }}
    >
      <CheckIcon size={10} className="text-[#22c55e]" />
      <span>{state.toast ?? ''}</span>
    </div>
  </JourneyStage>
)

const HomeView = () => (
  <JourneyView name="home" className="flex flex-col bg-white p-3">
    <h3 className="text-[10px] font-bold text-[#1c1917]">Hola, Automotora Central</h3>
    <p className="mb-2 text-[7px] text-[#706d71]">Resumen del mes en curso</p>

    <div className="mb-2 grid grid-cols-3 gap-1.5">
      {[
        ['Ventas del mes', '12'],
        ['Publicados', '36'],
        ['Consultas', '21'],
      ].map(([label, value]) => (
        <div key={label} className="rounded border border-[#e4e1e4] bg-[#faf8f8] p-1.5">
          <p className="font-mono text-[6px] uppercase text-[#706d71]">{label}</p>
          <p className="mt-0.5 text-[10px] font-bold text-[#1c1917]">{value}</p>
        </div>
      ))}
    </div>

    <div className="flex-1 rounded border border-[#e4e1e4] p-1.5">
      <p className="mb-1.5 text-[7px] font-bold text-[#1c1917]">Actividad reciente</p>
      <div className="space-y-1">
        {['bg-[#107c41]', 'bg-[#e5484d]', 'bg-stone-300'].map((dot, index) => (
          <div key={dot} className="flex items-center gap-1.5">
            <div className={`h-1 w-1 shrink-0 rounded-full ${dot}`} />
            <div className="h-1.5 animate-pulse rounded bg-stone-200" style={{ width: `${90 - index * 18}%` }} />
          </div>
        ))}
      </div>
    </div>
  </JourneyView>
)

const LoadingView = () => (
  <JourneyView name="loading" className="bg-white p-3">
    <div className="mb-2.5 h-2 w-1/3 animate-pulse rounded bg-stone-200" />
    <div className="space-y-1.5">
      {[100, 92, 80, 100, 66].map((width, index) => (
        <div key={index} className="h-1.5 animate-pulse rounded bg-stone-200" style={{ width: `${width}%` }} />
      ))}
    </div>
  </JourneyView>
)

const UsersView = () => {
  const member = useTarget('member')

  return (
    <JourneyView name="users" className="flex flex-col bg-white p-3">
      <h3 className="text-[10px] font-bold text-[#1c1917]">Gestionar Usuarios</h3>
      <p className="mb-2 text-[7px] text-[#706d71]">Miembros de Automotora Central</p>

      <div className="overflow-hidden rounded border border-[#e4e1e4]">
        <div className="grid grid-cols-[1.4fr_1fr_0.7fr] gap-1 border-b border-[#e4e1e4] bg-[#faf8f8] px-1.5 py-1">
          {['Nombre', 'Cargo', 'Estado'].map((column) => (
            <span key={column} className="font-mono text-[6px] uppercase text-[#706d71]">
              {column}
            </span>
          ))}
        </div>
        {TEAM.map((person) => (
          <div
            key={person.name}
            ref={person.primary ? member : undefined}
            className="grid grid-cols-[1.4fr_1fr_0.7fr] gap-1 border-b border-[#f2eeee] px-1.5 py-1.5 last:border-b-0"
          >
            <span className={`text-[7px] text-[#1c1917] ${person.primary ? 'font-medium' : ''}`}>{person.name}</span>
            <span className="text-[7px] text-[#706d71]">{person.job}</span>
            <span className="text-[6px] text-[#107c41]">{person.state}</span>
          </div>
        ))}
      </div>
    </JourneyView>
  )
}

const PermissionsView = ({ state }: ViewProps) => {
  const permissionLabel = useTarget('permissionLabel')
  const permissionBox = useTarget('permissionBox')
  const save = useTarget('save')

  return (
    <JourneyView name="permissions" className="flex flex-col bg-white p-3">
      <h3 className="text-[10px] font-bold text-[#1c1917]">Camilo Rojas</h3>
      <p className="mb-2 text-[7px] text-[#706d71]">Vendedor · Automotora Central</p>

      <p className="mb-1 font-mono text-[6px] uppercase text-[#706d71]">Permisos</p>

      {/* Same four-column grid and the same order as the real screen */}
      <div className="relative grid grid-cols-4 gap-x-1 gap-y-0.5">
        {PERMISSIONS.map((permission) => {
          const checked = permission.isTarget ? state.permissionChecked : permission.checked

          return (
            <div
              key={permission.label}
              className={`vk-perm-cell flex items-center gap-1 rounded px-1 py-[3px] ${toneClass[permission.tone]}`}
              data-highlighted={permission.isTarget && state.permissionHighlighted}
            >
              <span
                ref={permission.isTarget ? permissionBox : undefined}
                className={`vk-perm-box flex h-2 w-2 shrink-0 items-center justify-center rounded-[2px] border ${
                  checked ? 'border-[#e5484d]' : 'border-stone-300 bg-white'
                }`}
                data-checked={checked}
                data-tone="red"
              >
                {checked && <CheckIcon size={6} className="text-white" strokeWidth={4} />}
              </span>
              <span
                ref={permission.isTarget ? permissionLabel : undefined}
                className={`text-[5px] uppercase leading-tight tracking-[0.01em] ${permission.isTarget ? 'font-medium' : ''}`}
              >
                {permission.label}
                {permission.isTarget && (
                  <InfoIcon size={8} className="ml-0.5 inline-block align-text-top text-[#a8a29e]" />
                )}
              </span>
            </div>
          )
        })}

        {/* The real hover hint of the new label, shown below its row */}
        <div
          className="vk-pop pointer-events-none absolute left-0 top-[77%] z-20 w-[52%] rounded bg-[#1c1917] px-1.5 py-1 text-[5.5px] leading-snug text-white shadow"
          style={{ opacity: state.hintVisible ? 1 : 0, transform: `translateY(${state.hintVisible ? 0 : 3}px)` }}
        >
          {ROLE_HINT}
        </div>
      </div>

      <div className="mt-auto flex items-center justify-center gap-1.5 pt-2">
        <span ref={save} className="rounded bg-[#3b82f6] px-2 py-1 text-[7px] font-bold text-white">
          Grabar cambios
        </span>
        <span className="rounded bg-[#3b82f6] px-2 py-1 text-[7px] font-bold text-white">Cancelar</span>
      </div>
    </JourneyView>
  )
}

const ROW_GRID = 'grid grid-cols-[8px_8px_1fr_1.6fr_0.6fr_2fr] items-center gap-1.5'

const PublishView = ({ state }: ViewProps) => {
  const filtersToggle = useTarget('filtersToggle')
  const platformSelect = useTarget('platformSelect')
  const highlightSelect = useTarget('highlightSelect')
  const optionChileautos = useTarget('optionChileautos')
  const optionTopspot = useTarget('optionTopspot')

  return (
    <JourneyView name="publish" className="flex flex-col overflow-hidden bg-white p-2.5">
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="text-[10px] font-bold leading-none text-[#1c1917]">Vehículos Publicados</h3>
          <p className="mt-0.5 text-[6px] text-[#706d71]">Plataformas Externas</p>
        </div>
        <span ref={filtersToggle} className="text-[6.5px] font-bold text-[#2563eb]">
          {state.filtersOpen ? 'Esconder filtros de búsqueda' : 'Ver filtros de búsqueda'}
        </span>
      </div>

      {/* Plataformas, then Destacado only once ChileAutos is picked */}
      <div
        className="vk-fold mt-1.5"
        style={{
          maxHeight: state.filtersOpen ? 40 : 0,
          opacity: state.filtersOpen ? 1 : 0,
          overflow: state.filtersOpen ? 'visible' : 'hidden',
        }}
      >
        <div className="flex items-end gap-1.5 pb-1">
          <div className="w-[22%]">
            <p className="mb-0.5 font-mono text-[5px] uppercase text-[#706d71]">Año</p>
            <SelectBox value={null} placeholder="-- Todos --" />
          </div>
          <div className="relative w-[26%]">
            <p className="mb-0.5 font-mono text-[5px] uppercase text-[#706d71]">Plataformas</p>
            <SelectBox
              targetRef={platformSelect}
              value={state.platformChosen ? 'ChileAutos' : null}
              placeholder="-- Todas --"
              focused={state.menu === 'platform'}
            />
            <Dropdown open={state.menu === 'platform'}>
              {PLATFORM_OPTIONS.map((option) => (
                <DropdownOption
                  key={option}
                  targetRef={option === 'ChileAutos' ? optionChileautos : undefined}
                  label={option}
                  hovered={option === 'ChileAutos' && state.hoveredOption === 'chileautos'}
                />
              ))}
            </Dropdown>
          </div>
          <div
            className="vk-fold relative w-[30%]"
            style={{
              opacity: state.platformChosen ? 1 : 0,
              transform: state.platformChosen ? 'none' : 'translateX(-6px) scale(0.9)',
              overflow: 'visible',
            }}
          >
            <p className="mb-0.5 font-mono text-[5px] uppercase text-[#e5484d]">Destacado</p>
            <SelectBox
              targetRef={highlightSelect}
              value={state.highlightChosen ? 'TopSpot' : null}
              placeholder="-- Todos --"
              focused={state.menu === 'highlight'}
            />
            <Dropdown open={state.menu === 'highlight'}>
              {HIGHLIGHT_OPTIONS.map((option) => (
                <DropdownOption
                  key={option.value}
                  targetRef={option.value === 'topspot' ? optionTopspot : undefined}
                  label={option.label}
                  hovered={option.value === 'topspot' && state.hoveredOption === 'topspot'}
                />
              ))}
            </Dropdown>
          </div>
          <div className="whitespace-nowrap rounded border border-[#e5484d] px-1 py-[3px] text-[5.5px] text-[#e5484d]">
            Limpiar Filtros
          </div>
        </div>
      </div>

      <div className="mt-1.5 overflow-hidden rounded border border-[#e4e1e4]">
        <div className={`${ROW_GRID} border-b border-[#e4e1e4] bg-[#faf8f8] px-1.5 py-1`}>
          <span className="h-1.5 w-1.5 rounded-sm border border-[#d0cbd0]" />
          <span />
          {['Patente', 'Vehículo', 'Año', 'Plataformas'].map((column) => (
            <span key={column} className="font-mono text-[5.5px] uppercase text-[#706d71]">
              {column}
            </span>
          ))}
        </div>

        {VEHICLES.map((vehicle) => (
          <VehicleRow key={vehicle.plate} vehicle={vehicle} state={state} />
        ))}
      </div>
    </JourneyView>
  )
}

const VehicleRow = ({ vehicle, state }: { vehicle: (typeof VEHICLES)[number]; state: ChileautosHighlightsState }) => {
  const editRow = useTarget('editRow')
  const hidden = isRowHidden(vehicle.hiddenBy, state)
  const star = vehicle.isTarget ? (state.applied ? 'TopSpot' : undefined) : vehicle.star

  return (
    <>
      <div
        className={`vk-fold ${ROW_GRID} border-[#f2eeee] px-1.5 ${hidden ? 'border-b-0' : 'border-b'}`}
        style={{
          maxHeight: hidden ? 0 : 40,
          opacity: hidden ? 0 : 1,
          paddingTop: hidden ? 0 : 4,
          paddingBottom: hidden ? 0 : 4,
          backgroundColor: vehicle.isTarget && state.editorOpen ? 'rgba(229, 72, 77, 0.08)' : undefined,
        }}
      >
        <span className="h-1.5 w-1.5 rounded-sm border border-[#d0cbd0]" />
        <span ref={vehicle.isTarget ? editRow : undefined}>
          <SquarePenIcon size={8} className="text-[#e5484d]" />
        </span>
        <span className="text-[6.5px] font-bold">{vehicle.plate}</span>
        <span className="text-[6.5px]">{vehicle.model}</span>
        <span className="text-[6.5px]">{vehicle.year}</span>
        <span className="flex flex-wrap gap-0.5">
          {vehicle.platforms.map((platform) => (
            <span key={platform} className={`rounded px-1 text-[5.5px] ${PLATFORM_BADGE[platform]}`}>
              {platform}
            </span>
          ))}
          {star && (
            <span className={`rounded px-1 text-[5.5px] ${STAR_BADGE} ${vehicle.isTarget ? 'vk-badge-pop' : ''}`}>
              &#9733; {star}
            </span>
          )}
        </span>
      </div>

      {vehicle.isTarget && <RowEditor state={state} />}
    </>
  )
}

/** The inline editor of the LDCD96 row: tabs, sub-tabs, the Destacados panel. */
const RowEditor = ({ state }: ViewProps) => {
  const chaTab = useTarget('chaTab')
  const destacadosTab = useTarget('destacadosTab')

  return (
    <div
      className={`vk-fold border-[#e4e1e4] bg-white ${state.editorOpen ? 'border-b' : 'border-b-0'}`}
      style={{ maxHeight: state.editorOpen ? 140 : 0, opacity: state.editorOpen ? 1 : 0 }}
    >
      <div className="grid grid-cols-3 border-b border-[#f2eeee] text-center">
        <EditorTab label="MercadoLibre" active={!state.chaTab} />
        <EditorTab targetRef={chaTab} label="ChileAutos" active={state.chaTab} />
        <EditorTab label="Yapo" active={false} />
      </div>
      <div
        className="flex gap-2.5 border-b border-[#f2eeee] px-2 pt-1"
        style={{ visibility: state.chaTab ? 'visible' : 'hidden' }}
      >
        <span className="pb-0.5 text-[6px] text-[#706d71]">Detalles del Vehículo</span>
        <span className="pb-0.5 text-[6px] text-[#706d71]">Imágenes Públicas (8)</span>
        <span
          ref={destacadosTab}
          className={`border-b-[1.5px] pb-0.5 text-[6px] ${
            state.destacadosTab ? 'border-[#ea580c] text-[#ea580c]' : 'border-transparent text-[#706d71]'
          }`}
        >
          Destacados
        </span>
      </div>

      {state.destacadosTab ? (
        <HighlightsPanel state={state} />
      ) : (
        <div className="space-y-1 px-2 py-1.5">
          {[66, 50, 60].map((width) => (
            <div key={width} className="h-1.5 animate-pulse rounded bg-stone-200" style={{ width: `${width}%` }} />
          ))}
        </div>
      )}
    </div>
  )
}

/** Same sections and copy as the real Destacados tab. */
const HighlightsPanel = ({ state }: ViewProps) => {
  const apply = useTarget('apply')

  return (
    <div className="px-2 py-1.5">
      <div className="mb-1 flex items-center justify-between">
        <p className="text-[7px] font-bold text-[#1c1917]">Destacados en ChileAutos</p>
        <span className="rounded bg-[#fde8e8] px-1 py-0.5 text-[5.5px] text-[#e5484d]">Actualizar saldo</span>
      </div>
      <p className="mb-0.5 text-[6px] font-medium text-[#57534e]">En este aviso</p>
      {state.applied ? (
        <div className="vk-preview-fade mb-1 rounded border border-[#fde3b8] bg-[#fff8ec] px-1.5 py-0.5 text-[6px]">
          <span className="font-bold">TopSpot</span>
          <span className="text-[#706d71]"> · mensual · quedan 7 días</span>
        </div>
      ) : (
        <p className="mb-1 text-[5.5px] text-[#a8a29e]">Este aviso no tiene destacados aplicados desde Veekls.</p>
      )}
      <p className="mb-0.5 text-[6px] font-medium text-[#57534e]">Tus productos contratados</p>
      <div className="divide-y divide-[#f2eeee] rounded border border-[#f2eeee]">
        <div className="flex items-center justify-between px-1.5 py-0.5">
          <span className="text-[6px]">
            <b>Aviso Premium</b>
            <span className="text-[#706d71]"> · reutilizable · 3 de 3 disponibles</span>
          </span>
          {/* As on the real tab: with a TopSpot running, nothing else can be added */}
          {state.applied ? (
            <span className="text-[5px] text-[#706d71]">Disponible cuando venza el TopSpot</span>
          ) : (
            <span className="rounded bg-[#e5484d] px-1.5 py-0.5 text-[5.5px] text-white">Aplicar</span>
          )}
        </div>
        <div className="flex items-center justify-between px-1.5 py-0.5">
          <span className="text-[6px]">
            <b>TopSpot</b>
            <span className="text-[#706d71]"> · mensual · </span>
            <span className={state.applied ? 'font-semibold text-[#e5484d]' : 'text-[#706d71]'}>
              {state.applied ? '2 de 3 disponibles' : '3 de 3 disponibles'}
            </span>
          </span>
          {state.applied ? (
            <span className="text-[5px] text-[#706d71]">Ya aplicado</span>
          ) : (
            <span ref={apply} className="rounded bg-[#e5484d] px-1.5 py-0.5 text-[5.5px] text-white">
              Aplicar
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

/** The real ConfirmationModal: no title, the message, Si / No. */
const ConfirmDialog = ({ open }: { open: boolean }) => {
  const confirmYes = useTarget('confirmYes')

  return (
    <div
      className="vk-pop absolute inset-0 z-40 flex items-center justify-center bg-black/50"
      style={{ opacity: open ? 1 : 0, pointerEvents: 'none' }}
    >
      <div
        className="vk-pop w-[52%] rounded-md bg-white p-2.5 shadow-lg"
        style={{ transform: `scale(${open ? 1 : 0.92})` }}
      >
        <p className="mb-2 text-center text-[6.5px] leading-snug text-[#1c1917]">{CONFIRM_MESSAGE}</p>
        <div className="flex justify-around">
          <span ref={confirmYes} className="rounded bg-[#3b82f6] px-2.5 py-1 text-[6.5px] text-white">
            Si
          </span>
          <span className="rounded bg-[#d1d5db] px-2.5 py-1 text-[6.5px] text-[#1c1917]">No</span>
        </div>
      </div>
    </div>
  )
}

interface SelectBoxProps {
  targetRef?: (node: HTMLElement | null) => void
  value: string | null
  placeholder: string
  focused?: boolean
}

const SelectBox = ({ targetRef, value, placeholder, focused = false }: SelectBoxProps) => (
  <div
    ref={targetRef}
    className="flex justify-between rounded-[3px] border bg-white px-1 py-0.5 text-[6px] transition-all duration-200"
    style={{
      borderColor: focused ? '#e5484d' : '#e4e1e4',
      boxShadow: focused ? '0 0 0 2px rgba(229, 72, 77, 0.08)' : 'none',
    }}
  >
    <span className={value ? 'font-medium text-[#1c1917]' : 'text-[#706d71]'}>{value ?? placeholder}</span>
    <span className="text-[#a8a29e]">&#9662;</span>
  </div>
)

const Dropdown = ({ open, children }: { open: boolean; children: ReactNode }) => (
  <div
    className="vk-pop absolute left-0 right-0 top-full z-30 mt-0.5 rounded border border-[#e4e1e4] bg-white py-0.5 shadow-[0_6px_14px_-6px_rgba(28,25,23,0.25)]"
    style={{ opacity: open ? 1 : 0, transform: `translateY(${open ? 0 : -3}px)`, pointerEvents: 'none' }}
  >
    {children}
  </div>
)

interface DropdownOptionProps {
  targetRef?: (node: HTMLElement | null) => void
  label: string
  hovered: boolean
}

const DropdownOption = ({ targetRef, label, hovered }: DropdownOptionProps) => (
  <div
    ref={targetRef}
    className={`px-1.5 py-0.5 text-[6px] transition-colors ${hovered ? 'bg-[#e5484d]/10 text-[#e5484d]' : ''}`}
  >
    {label}
  </div>
)

interface EditorTabProps {
  targetRef?: (node: HTMLElement | null) => void
  label: string
  active: boolean
}

const EditorTab = ({ targetRef, label, active }: EditorTabProps) => (
  <span
    ref={targetRef}
    className={`border-b-[1.5px] py-1 text-[6.5px] transition-all duration-200 ${
      active ? 'border-[#2563eb] text-[#2563eb]' : 'border-transparent text-[#706d71]'
    }`}
  >
    {label}
  </span>
)
